import { Fragment, type ReactNode } from "react"
import _t from "@core/i18n"
import type { PriceType } from "@core/types"
import type { TableSection } from "@ionos-web-design-system/react"
import { Link } from "@ionos-web-design-system/react"
import DynamicUdsIcon from "~/components/DynamicUdsIcon"
import { ICON_SYNTAX_PATTERN } from "../constants"
import type { TableGroup, TableValue, Tooltip } from "../types"

type ParsedTableGroupName = {
  title: string
  icon: ReactNode
}

/**
 * A cell value after parsing from raw CMS string format.
 * - `true` / `false` — rendered as a checkmark or xmark icon
 * - `string` — rendered as plain text (or "-" when empty)
 */
type ProcessedValue = boolean | string

/**
 * Extracts and formats the price value from a string based on the current price type (netto or gross).
 *
 * CMS price value format: `[{netto=9,90 zł netto} {gross=12,18 zł gross}] / year`
 * For `currentPriceType = "netto"` this returns `"9,90 zł netto / year"`.
 *
 * @param value - Raw CMS cell value string potentially containing the price format token.
 * @param currentPriceType - Active price type (`"netto"` or `"gross"`) to extract from the token.
 *
 * @returns The formatted price string, or `null` if the input doesn't match the price format.
 */
function getFormattedPriceValue(
  value: string,
  currentPriceType: PriceType
): string | null {
  const startPriceTypeElement = value.indexOf(`[{`)
  const endPriceTypeElement = value.indexOf(`}]`)

  if (startPriceTypeElement === -1 || endPriceTypeElement === -1) {
    return null
  }

  // Find the segment for the requested price type, e.g. `{netto=9,90 zł netto}`
  const startPriceValueTextIndex = value.indexOf(`{${currentPriceType}=`)

  if (startPriceValueTextIndex === -1) {
    return null
  }

  const endPriceValueTextIndex = value.indexOf("}", startPriceValueTextIndex)

  if (endPriceValueTextIndex === -1) {
    return null
  }

  // Skip past `{<priceType>=` to get just the price string
  const price = value.substring(
    startPriceValueTextIndex + currentPriceType.length + 2,
    endPriceValueTextIndex
  )

  // Everything after `}]` is the billing unit, e.g. `/ year`
  const unit = value.substring(endPriceTypeElement + 2).trim()

  return `${price} ${unit}`
}

/**
 * Parses a raw CMS cell value into a typed `ProcessedValue`.
 *
 * Special CMS tokens:
 * - `[v]` / `[V]` → `true` (checkmark)
 * - `[x]` / `[X]` → `false` (xmark)
 * - price format `[{netto=...} {gross=...}]` → formatted price string for the active price type
 * - anything else → returned as-is
 *
 * @param value - Raw CMS cell value; non-strings are returned as `""`.
 * @param currentPriceType - Active price type (`"netto"` or `"gross"`), passed to `getFormattedPriceValue`.
 *
 * @returns A `ProcessedValue` ready for rendering.
 */
function parseTableValue(
  value: TableValue["value"],
  currentPriceType: PriceType
): ProcessedValue {
  if (typeof value !== "string") return ""
  if (value === "[v]" || value === "[V]") return true
  if (value === "[x]" || value === "[X]") return false

  return getFormattedPriceValue(value, currentPriceType) ?? value
}

/**
 * Converts a `ProcessedValue` into a React node suitable for rendering inside a UDS table cell.
 *
 * - `true`  → green checkmark icon
 * - `false` → red xmark icon
 * - string  → the string itself, or `"-"` when empty
 *
 * @param value - Parsed cell value to render.
 *
 * @returns A React node ready to be placed inside a UDS table cell.
 */
function getCellValue(value: ProcessedValue): ReactNode {
  if (value === true) {
    return (
      <DynamicUdsIcon
        iconName="checkmark"
        aria-label={_t("tableAriaLabels.iconTrue")}
        className="text-semantic-success-bolder"
      />
    )
  }

  if (value === false) {
    return (
      <DynamicUdsIcon
        iconName="xmark"
        aria-label={_t("tableAriaLabels.iconFalse")}
        className="text-semantic-danger-bolder"
      />
    )
  }

  return value || "-"
}

/**
 * Renders a tooltip value as a React node.
 *
 * A tooltip may be:
 * - a plain `string` — returned directly
 * - an array of strings and `TooltipLink` objects — rendered as inline text mixed with `<Link>` tags
 *
 * @param tooltip - Tooltip value from CMS.
 *
 * @returns A React node ready to be placed inside a UDS tooltip.
 */
function renderTooltipContent(tooltip: Tooltip): ReactNode {
  if (typeof tooltip === "string") {
    return tooltip
  }

  return (
    <>
      {tooltip.map((item) =>
        typeof item === "string" ? (
          <Fragment key={item}>{item}</Fragment>
        ) : (
          <Link key={item.href} href={item.href} title={item.title}>
            {item.text}
          </Link>
        )
      )}
    </>
  )
}

/**
 * Parses a CMS group name into a display title and an optional UDS icon.
 *
 * The CMS may embed an icon token anywhere in the name, e.g. `"[icon=bolt] Performance"`.
 * The token is stripped from the title, and the icon is rendered as a `DynamicUdsIcon` ReactNode.
 *
 * When the remaining title is empty or the placeholder `"_"`, the i18n fallback
 * `marketplace.basicDifferences` is used instead.
 *
 * @param name - Raw group name string from CMS.
 *
 * @returns An object with the resolved `title` and optional `icon` ReactNode.
 */
function parseTableGroupName(name: string): ParsedTableGroupName {
  const match = name.match(ICON_SYNTAX_PATTERN)

  const rawTitle = match
    ? name.replace(ICON_SYNTAX_PATTERN, "").trim()
    : name.trim()

  // An empty name or the "_" placeholder means "use the default section title"
  const title =
    !rawTitle || rawTitle === "_"
      ? _t("marketplace.basicDifferences")
      : rawTitle

  if (!match) {
    return { title, icon: undefined }
  }

  return { title, icon: <DynamicUdsIcon iconName={match[1].toLowerCase()} /> }
}

/**
 * Normalizes a `ProcessedValue` to a primitive (`string` or `boolean`) for equality comparison.
 *
 * Used by the UDS table to determine whether all cells in a row are identical
 *
 * @param value - Parsed cell value to normalize.
 *
 * @returns A `string` or `boolean` primitive suitable for equality comparison.
 */
function normalizeValueForComparison(value: ProcessedValue): string | boolean {
  if (typeof value === "boolean") return value

  return String(value ?? "")
}

/**
 * Transforms raw CMS `TableGroup[]` data into the `TableSection[]` shape expected by the UDS table component.
 *
 * Each group becomes a collapsible section. The first section is expanded by default.
 * Empty groups (no items) are filtered out before mapping.
 *
 * @param groups - Raw table groups from GraphQL / CMS.
 * @param currentPriceType - Active price type (`"netto"` or `"gross"`), used to format price cells.
 *
 * @returns Array of `TableSection` objects ready to be passed to the UDS `<Table>` component.
 */
export function getTableSections(
  groups: TableGroup[],
  currentPriceType: PriceType
): TableSection[] {
  return groups
    .filter((group) => group.items.length > 0)
    .map((group, index) => {
      const { title, icon } = parseTableGroupName(group.name)

      return {
        title,
        icon,
        isDefaultOpen: index === 0,
        rows: group.items.map((item) => {
          const values = item.values.map(({ value }: TableValue) =>
            parseTableValue(value, currentPriceType)
          )

          // A label can be either a plain string or a structured object with tooltip data
          const labelObj = typeof item.label === "string" ? null : item.label

          return {
            title: labelObj ? labelObj.title : (item.label as string),
            tooltip: labelObj?.tooltip
              ? renderTooltipContent(labelObj.tooltip)
              : undefined,
            cells: values.map(getCellValue),
            cellsToCompare: values.map(normalizeValueForComparison),
          }
        }),
      }
    })
}
